import { useEffect, useRef } from 'react';
import type { ExtractedEvent } from '../types';

interface Props {
  /** The event being rejected. When `null`, the modal is hidden. */
  event: ExtractedEvent | null;
  onClose: () => void;
  /**
   * Confirm handler. `flagForAiTraining=true` means the admin wants this
   * rejection added to the AI training review queue (i.e. the noise
   * filters / extractor prompt should have caught it upstream).
   */
  onConfirm: (event: ExtractedEvent, flagForAiTraining: boolean) => void;
}

/**
 * In-app confirmation modal for hiding/rejecting an event from the public
 * list. Built without Bootstrap's JS bundle (matches `EventDetailModal`
 * pattern) so the rest of the app doesn't have to load it.
 *
 * Two confirm actions:
 *   1. **Reject only** — marks the event `status='rejected'` (current behavior).
 *   2. **Reject & flag for AI training** — same rejection, plus records
 *      `flaggedForAiTraining=true` on the decision so we can review the
 *      noise filters/prompts later.
 */
export default function RejectEventModal({ event, onClose, onConfirm }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!event) return;
    const prev = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prev?.focus?.();
    };
  }, [event, onClose]);

  if (!event) return null;
  const label = event.title || 'this event';

  return (
    <div
      className="mh-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mh-reject-modal-title"
      onClick={onClose}
    >
      <div
        className="mh-modal-dialog mh-modal-dialog--sm"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="d-flex align-items-start gap-2 p-3 border-bottom">
          <h2 id="mh-reject-modal-title" className="h5 mb-0 flex-grow-1">
            Hide event
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          />
        </header>
        <div className="p-3">
          <p className="mb-2">
            Hide <strong>&ldquo;{label}&rdquo;</strong>? It will be marked
            rejected and removed from the public list. You can re-approve it
            from the Manage events page.
          </p>
          <p className="text-muted small mb-0">
            <strong>Reject &amp; flag for AI training</strong> also adds it
            to the AI training review queue (Ops &rarr; AI training) so we
            can tighten the noise filters / extractor prompt.
          </p>
        </div>
        <footer className="d-flex flex-wrap justify-content-end gap-2 p-3 border-top">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={() => onConfirm(event, false)}
          >
            <i className="bi bi-eye-slash me-1" aria-hidden="true" />
            Reject only
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => onConfirm(event, true)}
          >
            <i className="bi bi-robot me-1" aria-hidden="true" />
            Reject &amp; flag for AI training
          </button>
        </footer>
      </div>
    </div>
  );
}
